import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { telemetryService } from '../services/telemetry/telemetryService';
import type { StrokeSample as RowingArcStrokeSample } from '../features/dashboards/rowingArcTypes';

/**
 * Arc Metrics derived from telemetry_data via profile_median_by_angle RPC
 *
 * These metrics are calculated on-the-fly from gate angle and force traces,
 * NOT from a separate stroke_geometry table (which was created based on false assumptions).
 *
 * Data flow:
 * telemetry_data (gate_angle, gate_force_x) →
 * profile_median_by_angle_v1_fast RPC →
 * Calculate catch/finish angles, slip, effective length
 */
export interface ArcMetrics {
  /** Sternward kinematic extreme (signed gate angle, typically negative). */
  catchAngle: number;
  /** Bowward kinematic extreme (signed gate angle, typically positive). */
  finishAngle: number;
  effectiveLength: number; // Angular range where force exceeds Peach slip thresholds
  totalArc: number; // Kinematic span from catch to finish (degrees)
  /** False when the stroke window starts at/after catch threshold (no pre-catch samples). */
  catchSlipApplicable: boolean;
  catchSlipConfidence: 'high' | 'low';
  catchSlipAngle: number; // Angle from min gate angle to catch force threshold (degrees)
  finishSlipAngle: number; // Angle from finish force threshold to max gate angle (degrees)
  peakForce: number; // Maximum force during stroke (kgf)
  peakAngle: number; // Gate angle at peak force (degrees)
}

export interface StrokeSample {
  rowerPosition: number;
  strokeNumber: number; // 0 for median profile across all strokes
  metrics: ArcMetrics;
}

/**
 * Peach PowerLine slip thresholds (Coker 2010; Peach Arcs view documentation).
 * 196 N catch slip / 98 N finish slip, converted to kgf.
 *
 * Validated on 2V 1984 Cup export: 98 N finish threshold tracks Peach 0x800A
 * FinishSlip medians more closely than lower cutoffs (2–5 kgf inflate in-target %).
 */
export const PEACH_CATCH_FORCE_THRESHOLD_KGF = 196 / 9.80665;
export const PEACH_FINISH_FORCE_THRESHOLD_KGF = 98 / 9.80665;

const findFirstIndex = (length: number, predicate: (index: number) => boolean): number | null => {
  for (let i = 0; i < length; i++) {
    if (predicate(i)) {
      return i;
    }
  }
  return null;
};

/**
 * Analyze gate angle and force traces to derive arc metrics.
 *
 * Peach-aligned kinematic model (Arcs view):
 * 1. Catch angle = minimum gate angle in the stroke window
 * 2. Finish angle = maximum gate angle in the stroke window
 * 3. Catch slip = angle at catch force threshold minus minimum angle
 * 4. Finish slip = maximum angle minus angle when force drops below finish threshold (after peak)
 * 5. Effective length = total kinematic span minus both slips
 */
function computeArcMetricsFromArrays(
  angles: Float32Array,
  forces: Float32Array
): { metrics: ArcMetrics } | null {
  if (angles.length === 0 || forces.length === 0 || angles.length !== forces.length) {
    return null;
  }

  let thetaMin = angles[0];
  let thetaMax = angles[0];
  let peakForce = forces[0];
  let peakForceIdx = 0;

  for (let i = 1; i < angles.length; i++) {
    if (angles[i] < thetaMin) {
      thetaMin = angles[i];
    }
    if (angles[i] > thetaMax) {
      thetaMax = angles[i];
    }
    if (forces[i] > peakForce) {
      peakForce = forces[i];
      peakForceIdx = i;
    }
  }

  const peakAngle = angles[peakForceIdx];

  const catchThresholdIdx = findFirstIndex(
    angles.length,
    (i) => forces[i] >= PEACH_CATCH_FORCE_THRESHOLD_KGF
  );
  if (catchThresholdIdx == null) {
    return null;
  }

  const catchAngle = thetaMin;
  const finishAngle = thetaMax;
  const catchSlipAngle = Math.max(0, angles[catchThresholdIdx] - thetaMin);
  const catchSlipApplicable = catchThresholdIdx > 0;
  const catchSlipConfidence: 'high' | 'low' = catchSlipApplicable ? 'high' : 'low';

  let finishSlipAngle = 0;
  const finishThresholdIdx = findFirstIndex(
    angles.length,
    (i) => i > peakForceIdx && forces[i] < PEACH_FINISH_FORCE_THRESHOLD_KGF
  );
  if (finishThresholdIdx != null) {
    finishSlipAngle = Math.max(0, thetaMax - angles[finishThresholdIdx]);
  }

  const totalArc = thetaMax - thetaMin;
  const effectiveLength = Math.max(0, totalArc - catchSlipAngle - finishSlipAngle);

  return {
    metrics: {
      catchAngle,
      finishAngle,
      effectiveLength,
      totalArc,
      catchSlipApplicable,
      catchSlipConfidence,
      catchSlipAngle,
      finishSlipAngle,
      peakForce,
      peakAngle,
    },
  };
}

export function analyzeArcMetrics(angles: Float32Array, forces: Float32Array): ArcMetrics | null {
  return computeArcMetricsFromArrays(angles, forces)?.metrics ?? null;
}

export interface UseArcMetricsOptions {
  rowerPositions?: number[];
  enabled?: boolean;
}

/**
 * Hook to calculate arc metrics from telemetry_data
 *
 * Uses getGateForceMedianProfile (which calls profile_median_by_angle_v1_fast RPC)
 * to get force/angle traces, then derives catch/finish angles and slip metrics.
 *
 * Returns StrokeSample[] matching the interface expected by RowingArcD3 component.
 */
export const useArcMetrics = (sessionId: string | null, options?: UseArcMetricsOptions) => {
  const queryEnabled = (options?.enabled ?? true) && !!sessionId;

  const queryKey = useMemo(
    () => ['arc-metrics', sessionId, options?.rowerPositions?.join('|') ?? 'all'],
    [sessionId, options?.rowerPositions]
  );

  const query = useQuery<StrokeSample[]>({
    queryKey,
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    queryFn: async () => {
      if (!sessionId) {
        return [];
      }

      // Get profile data (force/angle traces) for specified rowers
      const profileResult = await telemetryService.getGateForceMedianProfile(sessionId, {
        rowerPositions: options?.rowerPositions,
        targetPoints: 180, // Default angular resolution
        minPointsPerStroke: 20, // Minimum data quality threshold
      });

      if (!profileResult || !profileResult.series) {
        return [];
      }

      const strokeSamples: StrokeSample[] = [];

      // Process each rower's profile data
      Object.entries(profileResult.series).forEach(([rowerKey, seriesData]) => {
        // Extract rower position from key (e.g., "rower1" → 1, "Stroke" → 8)
        const match = rowerKey.match(/rower(\d+)/);
        if (!match) {
          // Handle named positions like "Stroke", "Bow", etc.
          return;
        }

        const rowerPosition = parseInt(match[1], 10);
        const { angle, force } = seriesData;

        // Calculate arc metrics from angle/force traces
        const metrics = analyzeArcMetrics(angle, force);

        if (metrics) {
          strokeSamples.push({
            rowerPosition,
            strokeNumber: 0, // Median profile represents all strokes
            metrics,
          });
        }
      });

      return strokeSamples;
    },
  });

  return query;
};

const parseRowerPositionFromSeriesKey = (rowerKey: string): number | null => {
  const match = rowerKey.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Per-stroke arc samples for a session (sample_strokes_resampled RPC + client-side metrics).
 * Used by the coach arc prototype when a real session is selected.
 */
export interface UseArcSessionStrokeSamplesOptions {
  /** When false, the query does not run (e.g. prototype “sample data” mode). */
  enabled?: boolean;
}

export const useArcSessionStrokeSamples = (
  sessionId: string | null,
  options?: UseArcSessionStrokeSamplesOptions
) => {
  const queryEnabled = (options?.enabled ?? true) && !!sessionId;

  return useQuery<RowingArcStrokeSample[]>({
    queryKey: ['arc-session-stroke-samples', sessionId],
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      if (!sessionId) {
        return [];
      }

      const result = await telemetryService.getGateForceStrokeSamples(sessionId, {
        targetPoints: 180,
        minPointsPerStroke: 20,
      });

      if (!result?.series) {
        return [];
      }

      const out: RowingArcStrokeSample[] = [];

      for (const [rowerKey, row] of Object.entries(result.series)) {
        const rowerPosition = parseRowerPositionFromSeriesKey(rowerKey);
        if (rowerPosition == null || !row.strokes?.length) {
          continue;
        }

        for (const stroke of row.strokes) {
          const computed = computeArcMetricsFromArrays(stroke.angle, stroke.force);
          if (!computed) {
            continue;
          }
          const { metrics } = computed;

          out.push({
            strokeId: `${sessionId}-${rowerPosition}-${stroke.strokeIndex}`,
            label: `Stroke ${stroke.strokeIndex + 1}`,
            catchAngleDeg: Math.abs(metrics.catchAngle),
            finishAngleDeg: metrics.finishAngle,
            catchSlipApplicable: metrics.catchSlipApplicable,
            catchSlipConfidence: metrics.catchSlipConfidence,
            catchSlipDeg: metrics.catchSlipAngle,
            finishSlipDeg: metrics.finishSlipAngle,
            driveStartMs: null,
            driveTimeMs: null,
            recoveryTimeMs: null,
            dataSource: 'telemetry_strokes',
            confidence: null,
            strokeIndex: stroke.strokeIndex,
            rowerPosition,
          });
        }
      }

      return out.sort((a, b) => {
        if (a.rowerPosition !== b.rowerPosition) {
          return a.rowerPosition - b.rowerPosition;
        }
        return a.strokeIndex - b.strokeIndex;
      });
    },
  });
};
